import { useState, useCallback } from "react";
import { API_URL } from "../api";

interface LivenessSession {
  sessionId: string;
  sessionToken: string;
}

interface UseCreateLivenessSessionResult {
  createSession: () => Promise<LivenessSession>;
  isCreatingSession: boolean;
  sessionError: string | null;
  clearSessionError: () => void;
}

const useCreateLivenessSession = (): UseCreateLivenessSessionResult => {
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const clearSessionError = useCallback(() => setSessionError(null), []);

  const createSession = useCallback(async (): Promise<LivenessSession> => {
    setIsCreatingSession(true);
    setSessionError(null);

    try {
      const response = await fetch(
        `${API_URL}/account/user/account/liveness/session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({}),
        }
      );

      const data = await response.json();

      if (!response.ok || (!data.status && !data.success)) {
        const msg =
          data?.message || "Failed to initialise liveness. Please try again.";
        setSessionError(msg);
        throw new Error(msg);
      }

      const { sessionId, sessionToken } = data.data as LivenessSession;

      if (!sessionId || !sessionToken) {
        const msg = "Incomplete liveness session data from server.";
        setSessionError(msg);
        throw new Error(msg);
      }

      return { sessionId, sessionToken };
    } catch (err: any) {
      const msg =
        err?.message || "Failed to initialise liveness. Please try again.";
      setSessionError(msg);
      throw new Error(msg);
    } finally {
      setIsCreatingSession(false);
    }
  }, []);

  return { createSession, isCreatingSession, sessionError, clearSessionError };
};

export default useCreateLivenessSession;
